import {Component, OnDestroy, OnInit} from '@angular/core';
import {AnnualTotalValue} from '@core-app/types/annual-total-value.interface';
import {ContributionType} from '@core-app/types/contribution.interface';
import {BlockStatementAbstractComponent} from '../block-statement-abstract.component';

import {ContributionDataShape} from '@core-app/shapes/contribution-data-shape';
import {PillarType} from '@core-app/types/pillar.type';

@Component({
    selector: 'core-total-rewards-summary',
    templateUrl: './block-total-rewards-summary.component.html',
    standalone: false,
})
export class BlockTotalRewardsSummaryComponent
    extends BlockStatementAbstractComponent
    implements OnInit, OnDestroy {
    isBlankPageNeededForPdf!: boolean;
    rewardsSummaryHeading!: string;

    // Order: [Health, Time, Protection, Wealth]
    annualTotalValues: AnnualTotalValue[] = [];
    annualCompensationTotal!: string;
    annualRewardsTotal!: string;
    annualCompensationAndRewardsTotal!: string;

    pillars: PillarType[] = ['health', 'wealth', 'protection', 'time'];
    pillarExpandedStates!: boolean[];
    isPrintRequested!: boolean;

    isMobileTable?: boolean;

    override ngOnInit(): void {
        super.ngOnInit();
        this.isBlankPageNeededForPdf = this.trsService.showPdfBlankPage1();

        this.rewardsSummaryHeading = this.getFirstName()
            ? `${this.getFirstName()}'s Rewards`
            : 'Your Rewards';

        this.initializeAnnualTotalValueRadialBarChart();

        this.annualCompensationTotal = this.authUser.data['TOTAL_ANNUAL_COMPENSATION'] ?? 0;
        this.annualRewardsTotal = this.authUser.data['TOTAL_ANNUAL_REWARDS'] ?? 0;
        this.annualCompensationAndRewardsTotal = this.authUser.data['TOTAL_COMPENSATION_AND_REWARDS'] ?? 0;

        // Open first pillar in table
        this.pillarExpandedStates = [
            true,
            ...Array(this.pillars.length - 1).fill(false),
        ];

        // Observer to know when to show mobile or desktop table
        this.breakpoint.observe([
            '(max-width: 700px)'
        ]).subscribe(result => {
            this.isMobileTable = result.matches;
        });

        // Attach event listeners for print
        window.addEventListener('beforeprint', this.beforePrintHandler);
        window.addEventListener('afterprint', this.afterPrintHandler);
    }

    getFirstName(): string | null {
        if (!this.authUser) return null;

        return this.authUser.data['FIRST_NAME'] ?? '';
    }

    getCombinedPillarDataKeys(pillar: PillarType): (string | undefined)[] {
        return Object.values(ContributionDataShape[pillar]).flatMap(
            ({ee, er}) => [ee, er].filter(Boolean)
        );
    }

    getEEPillarDataKeys(pillar: PillarType): (string | undefined)[] {
        return Object.values(ContributionDataShape[pillar]).flatMap(({ee}) =>
            [ee].filter(Boolean)
        );
    }

    getERPillarDataKeys(pillar: PillarType): (string | undefined)[] {
        return Object.values(ContributionDataShape[pillar]).flatMap(({er}) =>
            [er].filter(Boolean)
        );
    }

    getPillarEETotal(pillar: PillarType): number {
        return (
            this.getEEPillarDataKeys(pillar)
                .map((dataKey: string | undefined) =>
                    parseFloat(this.authUser.data[dataKey ?? ''] || 0)
                )
                .reduce((sum: number, value: number) => sum + value, 0) ?? 0
        );
    }

    getPillarERTotal(pillar: PillarType): number {
        return (
            this.getERPillarDataKeys(pillar)
                .map((dataKey: string | undefined) =>
                    parseFloat(this.authUser.data[dataKey ?? ''] || 0)
                )
                .reduce((sum: number, value: number) => sum + value, 0) ?? 0
        );
    }

    getPillarPlans(pillar: PillarType): ContributionType[] {
        return Object.values(ContributionDataShape[pillar]).filter((data) => {
            return (this.authUser.data[data.ee ?? ''] || 0) !== 0 ||
                (this.authUser.data[data.er ?? ''] || 0) !== 0;
        });
    }

    getPlanTitle(plan: ContributionType): string {
        return this.trsService.getPlanTitle(plan);
    }

    togglePillar(index: number): void {
        this.pillarExpandedStates[index] = !this.pillarExpandedStates[index];
    }

    keyboardTogglePillar(event: KeyboardEvent, index: number): void {
        const pressedKey = event.code;
        if (pressedKey === 'Space' || pressedKey === 'Enter') {
            // Pressing space will leap the scroll position down;
            // prevent that
            event.preventDefault();

            this.togglePillar(index);
        }
    }

    initializeAnnualTotalValueRadialBarChart(): void {
        this.annualTotalValues = [
            {
                type: 'wealth',
                radius: 120,
                strokeColor: '#007bc7',
                strokeDashOffset: 0,
                rotationOffset: 0,
                strokeDashArray: 754,
                value: this.authUser.data['TOTAL_WEALTH'] ?? 0
            },
            {
                type: 'time',
                radius: 102,
                strokeColor: '#f4c7af',
                strokeDashOffset: 0,
                rotationOffset: 0,
                strokeDashArray: 640,
                value: this.authUser.data['TOTAL_TIME'] ?? 0
            },
            {
                type: 'protection',
                radius: 84,
                strokeColor: '#d9fc65',
                strokeDashOffset: 0,
                rotationOffset: 0,
                strokeDashArray: 525,
                value: this.authUser.data['TOTAL_PROTECTION'] ?? 0
            },
            {
                type: 'health',
                radius: 66,
                strokeColor: '#00f0bc',
                strokeDashOffset: 0,
                rotationOffset: 0,
                strokeDashArray: 412,
                value: this.authUser.data['TOTAL_HEALTH'] ?? 0
            },
        ];

        const maxTotalAnnualValue = Math.max(
            ...this.annualTotalValues.map((segment) => segment.value)
        );
        // NOTE: 20% of `maxTotalAnnualValue` is completely arbitrary
        // and is tweak-able depending on how close to 100% you'd like
        // your max segment value to be (lower is closer, higher is further)
        const maxTotalAnnualValueUpperbound =
            maxTotalAnnualValue + maxTotalAnnualValue * 0.3;

        // Calculate `stroke-dashoffset` so that every
        // segment's ring will be filled proportionally
        // NOTE 1: Every segment ring's `stroke-dasharray`
        // is different because the rings' sizes are different,
        // and this calculation takes that into account
        // NOTE 2: A lower `stroke-dashoffset` means a more
        // filled ring
        let cumulativeAngle = 0;

        this.annualTotalValues.forEach((segment) => {
            const ratio = Number(segment.value) / maxTotalAnnualValueUpperbound;
            const filledLength = ratio * segment.strokeDashArray;
            segment.strokeDashOffset = segment.strokeDashArray - filledLength;

            // Rotation in degrees for SVG's `transform`, computed from the cumulative angle
            segment.rotationOffset = cumulativeAngle;

            // Convert length of arc (filledLength) to degrees around the circle
            const angleForThisSegment =
                (filledLength / segment.strokeDashArray) * 360;
            cumulativeAngle += angleForThisSegment;
        });
    }

    getAnnualTotalValueCardLabel(type: PillarType): string {
        return `My ${type[0].toUpperCase() + type.slice(1)}`;
    }

    beforePrintHandler = () => {
        this.isPrintRequested = true;
    };

    afterPrintHandler = () => {
        this.isPrintRequested = false;
    };

    override ngOnDestroy() {
        // Cleanup listeners on component destruction
        window.removeEventListener('beforeprint', this.beforePrintHandler);
        window.removeEventListener('afterprint', this.afterPrintHandler);
    }
}
